import React, {Component} from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import cx from 'classnames';
import 'inert-polyfill'; // https://github.com/GoogleChrome/inert-polyfill
import AnimationGroup from '../AnimationGroup';
import {getOriginalBodyPadding, conditionallyUpdateScrollbar, setScrollbarWidth} from './modalUtils';
import styles from './Modal-hashed.scss';
import buttonStyles from '../button/Button-hashed.scss';
import { createContextConsumerHOC } from '@elixir/theme'; // FIX: should create elixir-common?
import { log } from '@elixir/logger';

// Default context with empty functions which is used when component is not wrapped within Modal
export const defaultModalContext = {
    dismissAction: () => log.warning('You should not use "dismissAction()" outside a <Modal>'),
    closeAction: () => log.warning('You should not use "closeAction()" outside a <Modal>')
};

export const ModalContext = React.createContext(defaultModalContext);

export function withModal(ComponentToWrap) {
    return createContextConsumerHOC({
        component: ComponentToWrap,
        context: ModalContext,
        displayName: `${ComponentToWrap.displayName || ComponentToWrap.name}WithModal`,
        mapContextToProps: (props, context) => context
    });
}

/**
 * A no-op function used as defaults for callbacks
 */
const noop = () => {
};

// attribute used to identify the elements added via the 'inert' attribute when the modal opens
const modalInertMarkerAttribute = 'modal-inert-marker';

const popoverAlignOffsets = {
    left: 0.1,
    center: 0.5,
    right: 0.9
};

/**
 * The `Modal` component is used by the `ModalProvider` as called by the `ModalService` to render a modal dialog to the
 * user.  This component is not meant to be directly imported and used by developers.  The `ModalService` APIs provide
 * the appropriate hooks to be able to configure the `Modal` dialog in any number of ways.
 */
export default class Modal extends Component {

    static propTypes = {
        /**
         * Optional function used to externally toggle the `isOpen` flag
         */
        toggle: PropTypes.func,
        /**
         * Optional flag indicating whether the Modal is currently open
         */
        isOpen: PropTypes.bool,
        /**
         * Optional flag indicating whether the Modal will autofocus to itself on open
         */
        autoFocus: PropTypes.bool,
        /**
         * Optional flag indicating whether keyboard support for closing the Modal is
         * available (via the ESC key)
         */
        keyboard: PropTypes.bool,
        /**
         * Optional flag that controls the visibility of the backdrop.  It can
         * only be one of three values, `true`, `false` or `static`.  True or static results in a semi-transparent
         * backdrop being shown, false does not.  Also, a static backdrop will not close the dialog when the
         * backdrop is clicked.
         */
        backdrop: PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.oneOf(['static'])
        ]),
        /**
         * Optional flag that controls whether the Modal dialog will expand itself to
         * take up the full screen
         */
        fullScreen: PropTypes.bool,
        /**
         * Optional flag that controls whether the Modal dialog has a wider footprint
         */
        wide: PropTypes.bool,
        /**
         * Callback function that, if provided, is called when the Modal component is mounted
         * but before the actual Modal dialog is opened
         */
        onEnter: PropTypes.func,
        /**
         * Callback function that, if provided, is called when the Modal component is unmounted
         * after the actual Modal dialog is closed and destroyed
         */
        onExit: PropTypes.func,
        /**
         * Callback function that, if provided, is called when the Modal dialog has been
         * opened and is visible to the user
         */
        onOpened: PropTypes.func,
        /**
         * Callback function that, if provided, is called when the Modal dialog has been
         * closed and is no longer visible to the user
         */
        onClosed: PropTypes.func,
        /**
         * The content of the Modal dialog that is rendered when it is opened
         */
        children: PropTypes.node,
        /**
         * The optional CSS class for the main window container for the Modal dialog
         */
        windowClass: PropTypes.string,
        /**
         * The zIndex value to use for the main window container for the Modal dialog
         */
        zIndex: PropTypes.number,
        /**
         * The optional "defer" object that is used to pass back the result to the caller.
         * The "defer" object's `reject()` method is called when the Modal dialog is dismissed while the
         * `resolve()` method is called when it is closed.
         */
        resultDefer: PropTypes.instanceOf(Object),
        /**
         * Optional flag that controls whether the Modal dialog will display as a popover
         */
        popOver: PropTypes.bool,
        /**
         * For 'popover', this controls whether the popover aligns to the left/center/right of trigger
         */
        popOverAlign: PropTypes.oneOf(['left', 'center', 'right'])
    };

    static defaultProps = {
        isOpen: false,
        autoFocus: true,
        keyboard: true,
        backdrop: true,
        fullScreen: false,
        wide: false,
        onOpened: noop,
        onClosed: noop,
        zIndex: 1050
    };

    static openCount = 0;

    /**
     * Constructs the Modal component, initializing the internal state of the component to null values
     *
     * @param {Object} props - The initial set of props for the component
     */
    constructor(props) {
        super(props);

        this._element = null;
        this.originalBodyPadding = null;
        this.isBodyOverflowing = false;

        this.state = {
            isOpen: props.isOpen,
        };

        if (props.isOpen) {
            this.init();
        }
    }

    /**
     * Callback that is used to properly "close" the dialog with a valid `result`
     *
     * @param {*} result - The result of the close, to be reported back to the caller if `resultDefer` is provided
     */
    closeModal = result => {
        const {resultDefer, toggle} = this.props;
        if (resultDefer) {
            resultDefer.resolve(result);
        }
        if (toggle) {
            toggle();
        }
    };

    /**
     * Callback that is used to dismiss the dialog with an invalid `result`
     *
     * @param {*} result - The result of the dismiss, to be reported back to the caller if `resultDefer` is provided
     */
    dismissModal = result => {
        const {resultDefer, toggle} = this.props;
        if (resultDefer) {
            resultDefer.reject(result);
        }
        if (toggle) {
            toggle();
        }
    };

    /**
     * React life-cycle method called when the component has mounted.  It will cause the Modal dialog to open if it
     * `isOpen` and it will callback to `onEnter` function if provided.
     */
    componentDidMount() {
        const {onEnter} = this.props;

        if (onEnter) {
            onEnter();
        }

        if (this.state.isOpen && this.props.autoFocus) {
            this.setFocus();
        }

        // FIX: is this needed here? Or can it work in onDialogRef?
        // if (this.props.popOver) {
        //     const dimensions = this._dialog.getBoundingClientRect();
        //     this.setState({ dimensions });
        // }
        if (this._dialog && this.props.popOver && !this.state.dimensions) {
            const dimensions = this._dialog.getBoundingClientRect();
            this.setState({ dimensions });
        }

        this._isMounted = true;
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.isOpen && !this.props.isOpen) {
            this.setState({ isOpen: nextProps.isOpen });
        }
    }

    componentWillUpdate(nextProps, nextState) {
        if (nextState.isOpen && !this.state.isOpen) {
            this.init();
        }
    }

    /**
     * React life-cycle method called when the component rerendered with updated properties.  It will open/close the
     * Modal dialog if the `isOpen` prop changed, otherwise it will rerender the Modal dialog back onto the page
     *
     * @param {Object} prevProps - The props from the previous render cycle
     * @param {Object} prevState - The state from the previous render cycle
     */
    componentDidUpdate(prevProps, prevState) {
        if (this.props.autoFocus && this.state.isOpen && !prevState.isOpen) {
            this.setFocus();
        }

        if (this._element && prevProps.zIndex !== this.props.zIndex) {
            this._element.style.zIndex = this.props.zIndex;
        }
    }

    /**
     * React life-cycle method called when the component has unmounted.  It will destroy the Modal dialog on the page
     * and callback to the `onExit` function if provided.
     */
    componentWillUnmount() {
        const {onExit} = this.props;
        if (onExit) {
            onExit();
        }

        if (this.state.isOpen) {
            this.destroy();
        }

        this._isMounted = false;
    }

    /**
     * Callback function passed to the `AnimationGroup` containing the Modal dialog that is called back once the "show"
     * animation has completed and the dialog is fully visible.  This will call the `onOpened` prop function.
     */
    onOpened = () => {
        this.props.onOpened();
    };

    /**
     * Callback function passed to the `AnimationGroup` containing the Modal dialog that is called back once the "hide"
     * animation has completed and the dialog is fully hidden.  This will call the `onClosed` prop function and set a
     * timer so that the Modal dialog in the DOM is destroyed.
     */
    onClosed = () => {
        // so all methods get called before it is unmounted
        this.props.onClosed();

        this.destroy();

        if (this._isMounted) {
            this.setState({ isOpen: false });
        }
    };

    /**
     * Set/clear the reference to the dialog component
     *
     * @param {ReactElement} dialog - The reference to the dialog component
     */
    onDialogRef = (dialog) => {
        this._dialog = dialog;
        // FIX: is this needed here? Or can it work in componentDidMount?
        // if (dialog && this.props.popOver && !this.state.dimensions) {
        //     const dimensions = dialog.getBoundingClientRect();
        //     this.setState({ dimensions });
        // }
    }

    /**
     * Set the focus on the first focusable element
     */
    setFocus() {
        if (this._dialog) {
            const focusableEl = this._dialog.querySelector(`a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not(.${styles.dismissButton}):not(.${buttonStyles.button}), [tabindex="0"]`);
            if (focusableEl) {
                focusableEl.focus();
            } else if (this._element) {
                this._element.focus();
            }
        }
    }

    /**
     * Callback function that deals with key processing and will dismiss the modal dialog when the ESC key is pressed
     * and the `keyboard` processing is active.
     *
     * @param {Object} e - The event for the key up
     */
    handleEscape = (e) => {
        if (this.props.isOpen && this.props.keyboard && e.keyCode === 27 && this.props.toggle) {
            this.dismissModal();
        }
    };

    /**
     * Callback function that deals with key processing and will dismiss the modal dialog when the ESC key is pressed
     * and the `keyboard` processing is active.
     *
     * @param {Object} e - The event for the key up
     */
    handleBackdropMouseDown = (e) => {
        this._mouseDownElement = e.target;
    }

    /**
     * Callback function that
     *
     * @param {Object} e - The event for the background click
     */
    handleBackdropClick = (e) => {
        if (e.target === this._mouseDownElement) {
            e.stopPropagation();

            if (!this.props.isOpen || this.props.backdrop !== true) return;

            const container = this._dialog;

            if (e.target && !container.contains(e.target) && this.props.toggle) {
                this.dismissModal();
            }
        }
    };

    /**
     * Create an on-the-fly container `div` on the document body that will be use to render the Modal dialog into the
     * browser.  Once the container is created, the whole of the Modal dialog (including the transitions container)
     * will be rendered into this container `div`
     */
    init() {
        try {
            this.focusedElBeforeOpen = document.activeElement;
        } catch (err) {
            this.focusedElBeforeOpen = null;
        }

        this._element = document.createElement('div');
        this._element.setAttribute('tabindex', '-1');
        this._element.style.position = 'relative';
        this._element.style.zIndex = this.props.zIndex;
        this.originalBodyPadding = getOriginalBodyPadding();

        conditionallyUpdateScrollbar();

        document.body.appendChild(this._element);
        if (Modal.openCount === 0) {
            document.body.classList.add(styles.modalOpened);
        }
        Modal.openCount += 1;

        // add 'inert' attribute to root elements to avoid them and their descendants to receive the focus
        Array.from(document.body.children).forEach(bodyChild => {
            if (bodyChild.contains(this._element)) return;
            bodyChild.setAttribute('inert', '');
            bodyChild.setAttribute(modalInertMarkerAttribute, '');
        });
    }

    /**
     * Destroys the on-the-fly container set up on the document body by the `init()` method, restoring things back to
     * is previous state.
     */
    destroy() {
        if (this._element) {
            document.body.removeChild(this._element);
            this._element = null;
        }

        // restore the focus to the element which had it before the modal was opened
        if (this.focusedElBeforeOpen) {
            this.focusedElBeforeOpen.focus();
            this.focusedElBeforeOpen = null;
        }

        if (Modal.openCount <= 1) {
            document.body.classList.remove(styles.modalOpened);
        }
        Modal.openCount -= 1;

        // remove 'inert' attributes added when modal was opened
        Array.from(document.querySelectorAll(`[${modalInertMarkerAttribute}]`)).forEach(modalInertMarkerEl => { // adding the ability for non-modal elements to get the focus
            modalInertMarkerEl.removeAttribute('inert');
            modalInertMarkerEl.removeAttribute(modalInertMarkerAttribute);
        });

        setScrollbarWidth(this.originalBodyPadding);
    }

    /**
     * Renders the container that represents the "dialog" portion of the Modal.  This includes proper styling and the
     * actual `children` element passed to the Modal.
     *
     * @returns {ReactElement} - The actual rendered Modal "dialog" portion of the Modal
     */
    renderModalDialog() {
        const {
            fullScreen, wide, popOver, popOverAlign = 'center', target, children, zIndex
        } = this.props;
        const { dimensions } = this.state;

        const modalClass = cx(
            {[styles.modalInnerFullScreen]: fullScreen},
            {[styles.modalInnerWide]: wide},
            {[styles.modalInnerPopOver]: popOver},
            {[styles.modalInnerPopOverLeft]: popOver && popOverAlign === 'left'},
            {[styles.modalInnerPopOverRight]: popOver && popOverAlign === 'right'},
            {[styles.modalInner]: !fullScreen && !wide && !popOver}
        );

        let innerStyles;
        if (popOver && target) {
            const rect = target.getBoundingClientRect();
            innerStyles = {};
            if (dimensions) {
                const width = dimensions.width;
                const offset = -(popoverAlignOffsets[popOverAlign]) || -(popoverAlignOffsets.center);
                innerStyles.top = rect.top + rect.height + 12;
                innerStyles.left = rect.left + (rect.width / 2) + (width * offset);
            } else {
                innerStyles.opacity = 0;
            }
        }

        return (
            <div style={{zIndex: zIndex + 10}}>
                <div className={modalClass} style={innerStyles} role="document" ref={this.onDialogRef}>
                    <ModalContext.Provider value={{ closeAction: this.closeModal, dismissAction: this.dismissModal }}>
                        {children}
                    </ModalContext.Provider>
                </div>
            </div>
        );
    }

    /**
     * Returns null since the Modal component doesn't render into the normal course the DOM, but rather will do DOM
     * manipulation to insert into the body when the Modal `isOpen`
     *
     * @returns {null} - The Modal component does not render anything
     */
    render() {
        if (this.state.isOpen) {
            const {
                windowClass, fullScreen, popOver, backdrop, isOpen, zIndex
            } = this.props;

            const modalAttributes = {
                onClick: this.handleBackdropClick,
                onMouseDown: this.handleBackdropMouseDown,
                onKeyUp: this.handleEscape,
                role: 'dialog',
                tabIndex: '-1'
            };
            const mainClass = cx(
                styles.modal,
                windowClass,
                {'fullScreen': fullScreen}
            );
            const backdropClasses = cx(
                styles.modalBackdrop,
                {[styles.in]: !popOver},
                {[styles.fade]: popOver}
            );

            const portalContent = (
                <div>
                    <AnimationGroup
                        animation="insert"
                        className={backdropClasses}
                        style={{zIndex}}
                        in={isOpen && !!backdrop}
                    >
                        <span/>
                    </AnimationGroup>
                    <AnimationGroup
                        animation="insert"
                        {...modalAttributes}
                        onEntered={this.onOpened}
                        onExited={this.onClosed}
                        className={mainClass}
                        in={isOpen}
                    >
                        {this.renderModalDialog()}
                    </AnimationGroup>
                </div>
            );

            return ReactDOM.createPortal(
                portalContent,
                this._element
            );
        }
        return null;
    }
}
